/**
 * CreditExhaustedPrompt
 *
 * Shows an upgrade prompt when AI credits are exhausted.
 * Replaces the AI action button when the workspace has no remaining credits.
 */

import { AlertTriangle, ArrowRight } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "@/packages/utils";

interface CreditExhaustedPromptProps {
  monthlyAllocation: number;
  workspaceSlug?: string;
  compact?: boolean;
}

export function CreditExhaustedPrompt({
  monthlyAllocation,
  workspaceSlug,
  compact = false,
}: CreditExhaustedPromptProps) {
  const { t } = useTranslation("common");
  const billingPath = workspaceSlug
    ? `/${workspaceSlug}/settings/billing`
    : "/settings/billing";

  if (compact) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-orange-500/30 bg-orange-500/5 px-3 py-2 text-xs text-orange-400">
        <AlertTriangle size={14} className="shrink-0" />
        <span>
          {t("ai.credits.allUsed", { count: monthlyAllocation })}{" "}
          <Link to={billingPath} className="underline hover:text-orange-300">
            {t("ai.credits.upgrade")}
          </Link>
        </span>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-orange-500/30 bg-orange-500/5 p-4 space-y-3">
      <div className="flex items-start gap-3">
        <div className="rounded-full bg-orange-500/10 p-2">
          <AlertTriangle size={18} className="text-orange-500" />
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium text-primary">
            {t("ai.credits.exhaustedTitle")}
          </p>
          <p className="text-xs text-secondary">
            {t("ai.credits.exhaustedDescription", { count: monthlyAllocation })}
          </p>
        </div>
      </div>
      <Link
        to={billingPath}
        className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent/90 transition-colors"
      >
        {t("ai.credits.upgradePlan")}
        <ArrowRight size={12} />
      </Link>
    </div>
  );
}
